import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { FiHash, FiGrid, FiBookOpen } from 'react-icons/fi';
import './IPUResultChecker.css';
import SEO from '../../components/SEO';
import BackButton from '../../components/BackButton';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
// PASTE YOUR CLOUDFLARE WORKER URL HERE
const CLOUDFLARE_WORKER_URL = 'https://cold-silence-6515.notesinfinity0061.workers.dev';

// Clean up trailing slash if present
const cleanBaseUrl = CLOUDFLARE_WORKER_URL.replace(/\/$/, '');

const API_CAPTCHA = `${cleanBaseUrl}/captcha`;
const API_LOGIN = `${cleanBaseUrl}/login`;

const STORAGE_KEY = 'ipu_result_data';

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────────

function marksToGrade(marks) {
    if (marks === null || marks === undefined || marks === '-') return '-';
    const m = parseFloat(marks);
    if (isNaN(m)) return '-';
    if (m >= 90) return 'O';
    if (m >= 80) return 'A+';
    if (m >= 70) return 'A';
    if (m >= 60) return 'B+';
    if (m >= 50) return 'B';
    if (m >= 40) return 'C';
    if (m >= 35) return 'P';
    return 'F';
}

function getGradeBadgeClass(grade) {
    switch (grade) {
        case 'O': return 'grade-o';
        case 'A+': return 'grade-aplus';
        case 'A': return 'grade-a';
        case 'B+': return 'grade-bplus';
        case 'B': return 'grade-b';
        case 'C': return 'grade-c';
        case 'P': return 'grade-p';
        default: return 'grade-f';
    }
}

function filterLatestAttempts(results) {
    const latestMap = {};
    results.forEach((r) => {
        const key = r.papercode || r.papername || r.subjectName;
        if (!latestMap[key]) {
            latestMap[key] = r;
        } else {
            const existingMarks = parseFloat(latestMap[key].moderatedprint || latestMap[key].totalMarks || 0);
            const currentMarks = parseFloat(r.moderatedprint || r.totalMarks || 0);
            if (currentMarks > existingMarks) latestMap[key] = r;
        }
    });
    return Object.values(latestMap);
}

function processResults(rawResults) {
    if (!rawResults || rawResults.length === 0) return null;
    const first = rawResults[0];
    const semesterMap = {};
    rawResults.forEach((r) => {
        const sem = r.euno || r.sem || 1;
        if (!semesterMap[sem]) semesterMap[sem] = [];
        semesterMap[sem].push(r);
    });

    const semesters = Object.entries(semesterMap)
        .map(([euno, subjects]) => {
            const eunoParsed = parseInt(euno, 10);
            const filteredSubjects = filterLatestAttempts(subjects);
            const totalMarks = filteredSubjects.reduce(
                (sum, s) => sum + (parseFloat(s.moderatedprint || s.totalMarks) || 0),
                0
            );
            const sgpa = parseFloat(subjects[0]?.eugpa || subjects[0]?.sgpa || 0) || 0;
            return { euno: eunoParsed, subjects, filteredSubjects, totalMarks: Math.round(totalMarks), sgpa };
        })
        .sort((a, b) => a.euno - b.euno);

    let cgpa = semesters.length > 0 ? semesters.reduce((sum, s) => sum + s.sgpa, 0) / semesters.length : 0;

    return {
        studentInfo: {
            name: first.stname || first.studentName || 'Student',
            enrollmentNumber: first.nrollno || first.enrollNo || '',
            institute: first.iname || first.instituteName || '',
            program: first.prgname || first.programName || '',
        },
        semesters,
        cgpa,
        allResults: rawResults,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function ErrorBanner({ message, onDismiss }) {
    if (!message) return null;
    return (
        <div className="ipu-error-banner">
            <span className="ipu-error-icon">⚠️</span>
            <span>{message}</span>
            {onDismiss && <button className="ipu-error-dismiss" onClick={onDismiss}>✕</button>}
        </div>
    );
}

function PortalNotice() {
    return (
        <div className="ipu-warning-box">
            <span className="ipu-warning-icon">📢</span>
            <div>
                <strong>Official Portal Instructions:</strong>
                <ul className="ipu-warning-list">
                    <li><strong>Password:</strong> Father's Name (<span className="ipu-highlight">CAPITAL LETTERS</span>, as per records).</li>
                    <li>Account will lock temporarily after <strong>3 failed attempts</strong>.</li>
                </ul>
            </div>
        </div>
    );
}

export default function IPUResultChecker() {
    const [step, setStep] = useState(1);
    const [enrollmentNumber, setEnrollmentNumber] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [captcha, setCaptcha] = useState('');
    const [captchaUrl, setCaptchaUrl] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCaptchaLoading, setIsCaptchaLoading] = useState(false);
    const [error, setError] = useState('');
    const [results, setResults] = useState(null);

    const data = useMemo(() => processResults(results), [results]);

    useEffect(() => {
        const cached = sessionStorage.getItem(STORAGE_KEY);
        if (cached) {
            try {
                setResults(JSON.parse(cached));
                setStep(3);
            } catch (e) {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        }
    }, []);

    const refreshCaptcha = useCallback(async () => {
        setIsCaptchaLoading(true);
        setError('');
        try {
            const res = await fetch(API_CAPTCHA);
            if (!res.ok) throw new Error('Could not fetch captcha. Verify worker is live.');

            const sid = res.headers.get('X-IPU-Session');
            setSessionId(sid || '');

            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            setCaptchaUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        } catch (e) {
            setError(`Bridge Error: ${e.message}`);
        } finally {
            setIsCaptchaLoading(false);
        }
    }, []);

    const handleNext = () => {
        if (!enrollmentNumber) return;
        setStep(2);
        refreshCaptcha();
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            console.log('[SnapED] Initiating IPU Portal Handshake...');
            const res = await fetch(API_LOGIN, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enrollmentNumber, password, captcha, sessionId })
            });

            const d = await res.json().catch(() => ({ success: false, error: 'Malformed response from bridge. The worker might be deploying.' }));

            if (!res.ok || d.success === false) {
                // Check if it's a 404 disguised as a success failure
                const errorMsg = d.error || `Authentication Failed (Status: ${res.status})`;
                throw new Error(errorMsg);
            }

            const raw = d.results || [];
            if (raw.length === 0) {
                throw new Error('Login successful, but no results found in the portal. Please verify you are looking at the correct semester.');
            }

            console.log('[SnapED] Portal Sync Successful. Parsing Results...');
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(raw));
            setResults(raw);
            setStep(3);
        } catch (e) {
            console.error('[SnapED] Login Flow Error:', e);
            setError(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const reset = () => {
        sessionStorage.removeItem(STORAGE_KEY);
        setResults(null);
        setStep(1);
        setEnrollmentNumber('');
        setPassword('');
        setCaptcha('');
        setError('');
    };

    return (
        <div className="ipu-page">
            <SEO
                title={data?.studentInfo?.name ? `Result: ${data.studentInfo.name}` : "IPU Result Checker 2024-25"}
                description={data?.studentInfo?.name
                    ? `View IPU B.Tech results for ${data.studentInfo.name} from ${data.studentInfo.institute}. SGPA, CGPA and subject-wise grades available.`
                    : "Check your GGSIPU B.Tech results instantly. Official SGPA/CGPA calculator, subject-wise grades and result history for all IPU students."}
                keywords={data?.studentInfo?.name
                    ? `${data.studentInfo.name}, ${data.studentInfo.enrollmentNumber}, IPU Result, GGSIPU Result`
                    : "IPU Result 2024, GGSIPU Result, IPU Exam Results, B.Tech Results IPU, SGPA Calculator IPU"}
            />
            <div className="ipu-bg-grid" />

            {/* Back Button */}
            {step !== 3 && (
                <div style={{ position: 'relative', zIndex: 2, maxWidth: '480px', margin: '0 auto 24px', padding: '0 16px' }}>
                    <BackButton to="/home" label="Back to Home" />
                </div>
            )}

            {step !== 3 && (
                <div className="ipu-page-hero">
                    <div className="ipu-hero-top-badge">
                        <span className="ipu-badge">2025-26 Season</span>
                        <span className="ipu-badge">Official Logic</span>
                    </div>
                    <h1 className="ipu-hero-title">IPU <span className="ipu-hero-accent">Result Portal</span></h1>
                    <p className="ipu-hero-subtitle">Premium Academic Dashboard for SnapED Students</p>
                </div>
            )}

            {step === 1 && (
                <div className="ipu-login-card">
                    <div className="ipu-login-header">
                        <div className="ipu-logo-wrap">
                            <div className="ipu-logo-glow"></div>
                            <div className="ipu-logo-icon">
                                <FiGrid size={28} />
                            </div>
                        </div>
                        <h2 className="ipu-login-title">Student Login</h2>
                        <p className="ipu-login-subtitle">Secure Access to Academic Records</p>
                    </div>

                    <PortalNotice />
                    <ErrorBanner message={error} onDismiss={() => setError('')} />

                    <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="ipu-form">
                        <div className="ipu-field">
                            <label>User Name</label>
                            <input
                                type="text"
                                className="ipu-input"
                                value={enrollmentNumber}
                                onChange={(e) => setEnrollmentNumber(e.target.value)}
                                placeholder="Enter enrollment number"
                                autoFocus
                                disabled={isLoading}
                            />
                        </div>
                        <button className="ipu-btn ipu-btn-primary" disabled={!enrollmentNumber || isLoading}>SECURE NEXT</button>
                        <p className="ipu-privacy-note">Your credentials are never stored. Verified securely via IPU portal.</p>
                    </form>
                </div>
            )}

            {step === 2 && (
                <div className="ipu-login-card">
                    <div className="ipu-login-header">
                        <h2 className="ipu-login-title">Portal Verification</h2>
                        <p className="ipu-login-subtitle">Session: {sessionId.substring(0, 8)}...</p>
                    </div>

                    <PortalNotice />
                    <ErrorBanner message={error} onDismiss={() => setError('')} />

                    <form onSubmit={handleLogin} className="ipu-form">
                        <div className="ipu-field">
                            <label>Password</label>
                            <div className="ipu-input-wrap">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    className="ipu-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Father's Name (CAPITALS)"
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className="ipu-eye-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    title={showPassword ? "Hide Password" : "Show Password"}
                                >
                                    {showPassword ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24M1 1l22 22" /></svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="ipu-field">
                            <label>Security Check</label>
                            <div className="ipu-captcha-row">
                                <input
                                    type="text"
                                    className="ipu-input ipu-captcha-input"
                                    value={captcha}
                                    onChange={(e) => setCaptcha(e.target.value)}
                                    placeholder="Code"
                                    disabled={isLoading}
                                />
                                <div className="ipu-captcha-image-wrap">
                                    {isCaptchaLoading ? <div className="ipu-skeleton-pulse" /> : <img src={captchaUrl} alt="captcha" className="ipu-captcha-img" />}
                                </div>
                                <button type="button" onClick={refreshCaptcha} className="ipu-btn ipu-btn-refresh" disabled={isCaptchaLoading}>↻</button>
                            </div>
                        </div>
                        <div className="ipu-form-actions">
                            <button type="button" onClick={() => setStep(1)} className="ipu-btn ipu-btn-ghost" disabled={isLoading}>Back</button>
                            <button className="ipu-btn ipu-btn-primary ipu-btn-flex" disabled={isLoading || !password || !captcha}>
                                {isLoading ? 'SECURE LOGGING...' : 'SECURE LOGIN'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {step === 3 && data && (
                <div className="ipu-results-container">
                    <div className="ipu-student-header">
                        <div className="ipu-student-top">
                            <div className="ipu-student-avatar">{data.studentInfo.name.charAt(0)}</div>
                            <div className="ipu-student-info">
                                <h2 className="ipu-student-name">{data.studentInfo.name}</h2>
                                <div className="ipu-student-meta">
                                    <span className="ipu-meta-chip"><FiHash size={12} /> {data.studentInfo.enrollmentNumber}</span>
                                    <span className="ipu-meta-chip"><FiBookOpen size={12} /> {data.studentInfo.program}</span>
                                </div>
                            </div>
                            <button onClick={reset} className="ipu-reset-btn">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>
                                New Search
                            </button>
                        </div>
                        <div className="ipu-sem-gpa-row">
                            <div className="ipu-gpa-card">
                                <span className="ipu-gpa-label">Overall CGPA</span>
                                <span className="ipu-gpa-value">{data.cgpa.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="ipu-table-card">
                        <div className="ipu-table-header">
                            <h3>Academic Record (All Semesters)</h3>
                        </div>
                        <div className="ipu-table-scroll">
                            <table className="ipu-table">
                                <thead>
                                    <tr>
                                        <th>Paper Code</th>
                                        <th className="ipu-th-wide">Subject Name</th>
                                        <th>Marks</th>
                                        <th>Grade</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.allResults.map((r, i) => {
                                        const g = marksToGrade(r.moderatedprint || r.totalMarks);
                                        return (
                                            <tr key={i} className="ipu-tr">
                                                <td className="ipu-td ipu-mono">{r.papercode}</td>
                                                <td className="ipu-td">{r.papername || r.subjectName}</td>
                                                <td className="ipu-td ipu-td-bold">{r.moderatedprint || r.totalMarks}</td>
                                                <td className="ipu-td"><span className={`ipu-grade-badge ${getGradeBadgeClass(g)}`}>{g}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
